/**
 * Client SDK utilities: timestamp schema and converter factory.
 * Kept in a separate module so document modules (healthcheck, perHashReferralData)
 * can import them without creating a circular dependency with index.ts.
 */
import type {
  FirestoreDataConverter,
  DocumentData,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from 'firebase/firestore';
import {z} from 'zod';
import {Timestamp} from 'firebase/firestore';

import {type ConverterOptions, createTimestampSchema} from '../types';

// ============================================================================
// Client Timestamp Schema
// ============================================================================

/**
 * Zod schema that validates and transforms timestamp data to Timestamp instances.
 * Handles both Timestamp instances and raw JSON data from serialization.
 *
 * Official API: https://firebase.google.com/docs/reference/js/firestore_.timestamp
 * Serialization behavior: https://github.com/firebase/firebase-admin-node/issues/1404
 */
export const ClientTimestampSchema = createTimestampSchema({
  TimestampClass: Timestamp,
  isInstance: (v): v is Timestamp => v instanceof Timestamp,
});

// ============================================================================
// Converter Factory
// ============================================================================

/**
 * Creates a FirestoreDataConverter for a given Zod schema.
 * Throws ZodError if validation is enabled and fails.
 *
 * NOTE: createConverter is intentionally duplicated between client and admin
 * because the FirestoreDataConverter interfaces differ — the client SDK's
 * fromFirestore accepts a SnapshotOptions parameter, while the admin SDK's
 * does not.
 */
export function createConverter<T>({
  schema,
  options,
}: {
  schema: z.ZodType<T>;
  options: ConverterOptions;
}): FirestoreDataConverter<T> {
  return {
    toFirestore(data: T): DocumentData {
      let result: DocumentData = options.validateOnWrite
        ? (schema.parse(data) as DocumentData)
        : (data as DocumentData);
      if (
        options.augmentOnWrite &&
        Object.keys(options.augmentOnWrite).length > 0
      ) {
        result = {...result};
        for (const [key, getValue] of Object.entries(options.augmentOnWrite)) {
          result[key] = getValue();
        }
      }
      return result;
    },
    fromFirestore(snapshot: QueryDocumentSnapshot, opts?: SnapshotOptions): T {
      // Resolve a not-yet-acknowledged `serverTimestamp()` sentinel (present on
      // the optimistic local snapshot right after a write) to a client-side
      // estimate rather than null. Otherwise a `validateOnRead` schema that
      // requires a Timestamp throws on that transient snapshot — which surfaces
      // as a full-page router error until the server ack arrives. Callers can
      // still override via `opts`.
      const data = snapshot.data({serverTimestamps: 'estimate', ...opts});
      if (options.validateOnRead) {
        return schema.parse(data);
      }
      return data as T;
    },
  };
}
